/**
 * Create Insurance Actor
 *
 * Creates one or more insurance records with EHR integration.
 * Handles both primary (priority 1) and secondary (priority 2) insurance.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { InsurancesStatusEnum } from "@frontend/lib/graphql/generated-gql";
import { fromPromise } from "xstate";
import { z } from "zod";
import {
  type CreateInsurancesWithEhrSuccess,
  type InsuranceInput,
  deleteAndCreateInsurances,
  updateInsurance,
  updateInsuranceStatus,
} from "../../server-actions/insurance-actions";

const InsuranceSchema = z.object({
  insuranceId: z.string().optional(),
  memberId: z.string().optional(),
  payerId: z.string().optional(),
  payer: z.string().optional(),
  state: z.string().optional(),
  ehrInsuranceId: z.string().optional(),
  ehrInsurancePackageId: z.string().optional(),
  status: z.nativeEnum(InsurancesStatusEnum),
  coverageOverview: z
    .object({
      groupNumber: z.string().optional().nullable(),
      planName: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  subscriberInfo: z
    .object({
      subscriberDob: z.string().optional().nullable(),
      subscriberSex: z.string().optional().nullable(),
      subscriberFirstName: z.string().optional().nullable(),
      subscriberLastName: z.string().optional().nullable(),
      relationshipToInsured: z.string().optional().nullable(),
      relationshipToInsuredCode: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  insuredFirstName: z.string().optional().nullable(),
  insuredLastName: z.string().optional().nullable(),
});

/**
 * Background Insurance Schema (minimal fields for duplicate detection)
 * Only includes fields needed for comparison (payer, memberId, state) and reuse (insuranceId, ehrInsuranceId)
 */
const BackgroundInsuranceSchema = z.object({
  insuranceId: z.string().optional(),
  memberId: z.string().optional(),
  payer: z.string().optional(),
  state: z.string().optional(),
  ehrInsuranceId: z.string().optional().nullable(),
  ehrInsurancePackageId: z.string().optional().nullable(),
});

/**
 * Input Schema (What this actor needs)
 * Accepts an object with primaryInsurance and optionally secondaryInsurance
 * Also accepts background insurance fields for duplicate detection (from skipped OTP flow)
 */
const CreateInsuranceInputSchema = z.object({
  primaryInsurance: InsuranceSchema.optional(),
  secondaryInsurance: InsuranceSchema.optional(),
  consumerId: z.string().min(1),
  orgId: z.number(),
  // Background fields for duplicate detection (set by clearInsurance when user skips OTP)
  // Uses minimal schema - only fields needed for comparison and reuse
  primaryInsuranceBackground: BackgroundInsuranceSchema.optional().nullable(),
  secondaryInsuranceBackground: BackgroundInsuranceSchema.optional().nullable(),
  // When true, insurance input fields changed since last verification — trigger update for existing records
  insuranceInputChanged: z.boolean().optional(),
});

type CreateInsuranceInput = z.infer<typeof CreateInsuranceInputSchema>;

/**
 * Success Schema (What this actor returns on success)
 */
const CreateInsuranceSuccessSchema = z.object({
  insurances: z.array(
    z.object({
      insuranceId: z.string(),
      priority: z.number(),
      ehrInsuranceId: z.string().optional(),
      ehrInsurancePackageId: z.string().optional(),
    }),
  ),
});

/**
 * Error Schema (What this actor returns on failure)
 */
const CreateInsuranceErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "INSURANCE_CREATE_FAILED",
    "NETWORK_ERROR",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

type CreateInsuranceError = z.infer<typeof CreateInsuranceErrorSchema>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
  composedActors?: readonly string[];
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Helper to check if two insurances match by key fields (payer + memberId + state)
 * Used for duplicate detection when user skips OTP and enters same insurance
 */
function insurancesMatch(
  entered: { payer?: string; memberId?: string; state?: string } | undefined,
  background:
    | { payer?: string; memberId?: string; state?: string }
    | undefined
    | null,
): boolean {
  if (!entered || !background) return false;

  // Normalize and compare key fields
  const enteredPayer = entered.payer?.toLowerCase().trim();
  const backgroundPayer = background.payer?.toLowerCase().trim();
  const enteredMemberId = entered.memberId?.toLowerCase().trim();
  const backgroundMemberId = background.memberId?.toLowerCase().trim();
  const enteredState = entered.state?.toLowerCase().trim();
  const backgroundState = background.state?.toLowerCase().trim();

  return (
    enteredPayer === backgroundPayer &&
    enteredMemberId === backgroundMemberId &&
    enteredState === backgroundState
  );
}

/**
 * Create Insurance Actor Implementation
 */
const createInsuranceActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: CreateInsuranceInput;
  }): Promise<CreateInsurancesWithEhrSuccess> => {
    // Validate input
    const validated = CreateInsuranceInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: CreateInsuranceError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Check for duplicate insurance (user skipped OTP and entered same insurance)
    // If entered insurance matches background insurance, use the existing insuranceId
    let primaryInsuranceId = validated.data.primaryInsurance?.insuranceId;
    let secondaryInsuranceId = validated.data.secondaryInsurance?.insuranceId;

    if (
      !primaryInsuranceId &&
      insurancesMatch(
        validated.data.primaryInsurance,
        validated.data.primaryInsuranceBackground,
      )
    ) {
      primaryInsuranceId =
        validated.data.primaryInsuranceBackground?.insuranceId;
      datadogLogs.logger.info(
        "[createInsuranceActor] Primary insurance matches background, reusing existing insuranceId",
        { insuranceId: primaryInsuranceId },
      );
    }

    if (
      !secondaryInsuranceId &&
      insurancesMatch(
        validated.data.secondaryInsurance,
        validated.data.secondaryInsuranceBackground,
      )
    ) {
      secondaryInsuranceId =
        validated.data.secondaryInsuranceBackground?.insuranceId;
      datadogLogs.logger.info(
        "[createInsuranceActor] Secondary insurance matches background, reusing existing insuranceId",
        { insuranceId: secondaryInsuranceId },
      );
    }

    // Update existing insurance records if they have an ID but data has changed (PLA-1213)
    // This handles the case where a returning patient edits their insurance fields
    // (e.g., corrects memberId or insured name via OCR)
    const primaryNeedsUpdate =
      primaryInsuranceId &&
      validated.data.primaryInsurance &&
      !insurancesMatch(
        validated.data.primaryInsurance,
        validated.data.primaryInsuranceBackground,
      );
    const secondaryNeedsUpdate =
      secondaryInsuranceId &&
      validated.data.secondaryInsurance &&
      !insurancesMatch(
        validated.data.secondaryInsurance,
        validated.data.secondaryInsuranceBackground,
      );

    if (primaryNeedsUpdate || secondaryNeedsUpdate) {
      const updateResults: CreateInsurancesWithEhrSuccess["insurances"] = [];

      if (
        primaryNeedsUpdate &&
        primaryInsuranceId &&
        validated.data.primaryInsurance
      ) {
        const ins = validated.data.primaryInsurance;
        const result = await updateInsurance({
          orgId: validated.data.orgId,
          id: primaryInsuranceId,
          insurance: {
            memberId: ins.memberId,
            priority: 1,
            payer: ins.payer,
            state: ins.state,
            groupNumber: ins.coverageOverview?.groupNumber ?? undefined,
            planName: ins.coverageOverview?.planName ?? undefined,
            insuredFirstName: ins.insuredFirstName ?? undefined,
            insuredLastName: ins.insuredLastName ?? undefined,
            subscriberInfo: ins.subscriberInfo ?? undefined,
          },
        });

        if (!result.success) {
          const errorData: CreateInsuranceError = {
            code: result.error.code as CreateInsuranceError["code"],
            message: result.error.message,
            retryable: result.error.retryable,
          };
          datadogLogs.logger.error(
            "[createInsuranceActor] Failed to update primary insurance:",
            errorData,
          );
          const error = new Error(errorData.message);
          Object.assign(error, errorData);
          throw error;
        }

        // Update status from verification result
        if (ins.status) {
          await updateInsuranceStatus(primaryInsuranceId, ins.status);
        }

        updateResults.push({
          insuranceId: primaryInsuranceId,
          priority: 1,
          ehrInsuranceId: result.data.ehrInsuranceId,
          ehrInsurancePackageId: result.data.ehrInsurancePackageId,
        });
      }

      if (
        secondaryNeedsUpdate &&
        secondaryInsuranceId &&
        validated.data.secondaryInsurance
      ) {
        const ins = validated.data.secondaryInsurance;
        const result = await updateInsurance({
          orgId: validated.data.orgId,
          id: secondaryInsuranceId,
          insurance: {
            memberId: ins.memberId,
            priority: 2,
            payer: ins.payer,
            state: ins.state,
            groupNumber: ins.coverageOverview?.groupNumber ?? undefined,
            planName: ins.coverageOverview?.planName ?? undefined,
            insuredFirstName: ins.insuredFirstName ?? undefined,
            insuredLastName: ins.insuredLastName ?? undefined,
            subscriberInfo: ins.subscriberInfo ?? undefined,
          },
        });

        if (!result.success) {
          const errorData: CreateInsuranceError = {
            code: result.error.code as CreateInsuranceError["code"],
            message: result.error.message,
            retryable: result.error.retryable,
          };
          datadogLogs.logger.error(
            "[createInsuranceActor] Failed to update secondary insurance:",
            errorData,
          );
          const error = new Error(errorData.message);
          Object.assign(error, errorData);
          throw error;
        }

        // Update status from verification result
        if (ins.status) {
          await updateInsuranceStatus(secondaryInsuranceId, ins.status);
        }

        updateResults.push({
          insuranceId: secondaryInsuranceId,
          priority: 2,
          ehrInsuranceId: result.data.ehrInsuranceId,
          ehrInsurancePackageId: result.data.ehrInsurancePackageId,
        });
      }

      if (updateResults.length > 0) {
        datadogLogs.logger.info(
          "[createInsuranceActor] Updated existing insurance(s):",
          updateResults,
        );
        return { insurances: updateResults };
      }
    }

    // Build insurances array from primary and secondary
    const insurances: InsuranceInput[] = [];

    if (
      !primaryInsuranceId &&
      validated.data.primaryInsurance?.memberId &&
      validated.data.primaryInsurance?.payer &&
      validated.data.primaryInsurance?.state &&
      validated.data.primaryInsurance?.payerId
    ) {
      insurances.push({
        payerId: validated.data.primaryInsurance.payerId,
        memberId: validated.data.primaryInsurance.memberId,
        payer: validated.data.primaryInsurance.payer,
        state: validated.data.primaryInsurance.state,
        status: validated.data.primaryInsurance.status,
        groupNumber:
          validated.data.primaryInsurance.coverageOverview?.groupNumber ??
          undefined,
        priority: 1,
        ehrInsuranceId: validated.data.primaryInsurance.ehrInsuranceId,
        ehrInsurancePackageId:
          validated.data.primaryInsurance.ehrInsurancePackageId,
        planName:
          validated.data.primaryInsurance.coverageOverview?.planName ??
          undefined,
        subscriberInfo:
          validated.data.primaryInsurance.subscriberInfo ?? undefined,
      });
    }

    if (
      !secondaryInsuranceId &&
      validated.data.secondaryInsurance?.memberId &&
      validated.data.secondaryInsurance?.payer &&
      validated.data.secondaryInsurance?.state &&
      validated.data.secondaryInsurance?.payerId
    ) {
      insurances.push({
        memberId: validated.data.secondaryInsurance.memberId,
        payerId: validated.data.secondaryInsurance.payerId,
        payer: validated.data.secondaryInsurance.payer,
        state: validated.data.secondaryInsurance.state,
        status: validated.data.secondaryInsurance.status,
        priority: 2,
        ehrInsuranceId: validated.data.secondaryInsurance.ehrInsuranceId,
        groupNumber:
          validated.data.secondaryInsurance.coverageOverview?.groupNumber ??
          undefined,
        ehrInsurancePackageId:
          validated.data.secondaryInsurance.ehrInsurancePackageId,
        planName:
          validated.data.secondaryInsurance.coverageOverview?.planName ??
          undefined,
        subscriberInfo:
          validated.data.secondaryInsurance.subscriberInfo ?? undefined,
      });
    }

    // If no new insurances to create, return existing insuranceIds from background match
    if (insurances.length === 0) {
      // Check if we have any existing insuranceIds from background match
      const existingInsurances: CreateInsurancesWithEhrSuccess["insurances"] =
        [];

      if (primaryInsuranceId) {
        existingInsurances.push({
          insuranceId: primaryInsuranceId,
          priority: 1,
          ehrInsuranceId:
            validated.data.primaryInsuranceBackground?.ehrInsuranceId ??
            undefined,
          ehrInsurancePackageId:
            validated.data.primaryInsuranceBackground?.ehrInsurancePackageId ??
            undefined,
        });
      }

      if (secondaryInsuranceId) {
        existingInsurances.push({
          insuranceId: secondaryInsuranceId,
          priority: 2,
          ehrInsuranceId:
            validated.data.secondaryInsuranceBackground?.ehrInsuranceId ??
            undefined,
          ehrInsurancePackageId:
            validated.data.secondaryInsuranceBackground
              ?.ehrInsurancePackageId ?? undefined,
        });
      }

      if (existingInsurances.length > 0) {
        datadogLogs.logger.info(
          "[createInsuranceActor] All insurances matched background, skipping creation",
          { existingInsurances },
        );
        return { insurances: existingInsurances };
      }

      // No valid insurance data at all
      const errorData: CreateInsuranceError = {
        code: "INVALID_INPUT",
        message: "No valid insurance data provided",
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    // Mark old background insurances as Deleted before creating new ones.
    // When the user switched insurance (background exists but didn't match the
    // new entry), the old record should be marked Deleted so it's excluded
    // from downstream queries.
    const idsToDelete: string[] = [];
    if (
      !primaryInsuranceId &&
      validated.data.primaryInsuranceBackground?.insuranceId &&
      insurances.some((ins) => ins.priority === 1)
    ) {
      idsToDelete.push(validated.data.primaryInsuranceBackground.insuranceId);
    }
    if (
      !secondaryInsuranceId &&
      validated.data.secondaryInsuranceBackground?.insuranceId &&
      insurances.some((ins) => ins.priority === 2)
    ) {
      idsToDelete.push(validated.data.secondaryInsuranceBackground.insuranceId);
    }

    // Delete old insurances (fire-and-forget) and create new ones in a single call
    const result = await deleteAndCreateInsurances({
      insuranceIdsToDelete: idsToDelete,
      createInput: {
        insurances,
        consumerId: validated.data.consumerId,
        orgId: validated.data.orgId,
      },
    });

    if (!result.success) {
      const errorData: CreateInsuranceError = {
        code: result.error.code as CreateInsuranceError["code"],
        message: result.error.message,
        retryable: result.error.retryable,
      };

      datadogLogs.logger.error(
        "[createInsuranceActor] Failed to create insurance:",
        errorData,
      );

      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    datadogLogs.logger.log(
      "[createInsuranceActor] Successfully created insurance(s):",
      result.data,
    );

    return result.data;
  },
);

/**
 * Create Insurance Actor with Metadata
 */
export const createInsuranceActor: ActorWithMetadata = {
  impl: createInsuranceActorImpl,
  metadata: {
    displayName: "createInsurance",
    description:
      "Create one or more insurance records with EHR integration (primary and/or secondary)",
    category: "data",
    retryable: true,
    timeout: 20000, // 20 seconds (may need to create 2 insurances)
    errorCodes: [
      "INVALID_INPUT",
      "INSURANCE_CREATE_FAILED",
      "NETWORK_ERROR",
      "UNKNOWN_ERROR",
    ],
    inputSchema: CreateInsuranceInputSchema,
    successSchema: CreateInsuranceSuccessSchema,
    errorSchema: CreateInsuranceErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as CreateInsurancesWithEhrSuccess;
      return {
        createdInsurances: success.insurances,
        insuranceCreated: true,
      };
    },
    onError: (error: unknown) => {
      const err = error as CreateInsuranceError;
      return {
        insuranceCreated: false,
        insuranceCreationError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
